import * as R from 'ramda';

import {
	ContactImportIntegrationNameEnum,
	ContactImportIntegrationDisplayNameEnum,
	WorkspacePlansEnum,
	WorkspaceEntitlementFeatureEnum,
} from '@app/constants/modules/workspace';
import {
	IntegrationListType,
	TemplateType,
	WorkspaceFeatureStatus,
	WorkspaceType,
} from '@app/redux/workspace/types';
import { convertToTimezone } from '@app/utils/convertToTimezone';
import {
	CampaignBuilderEnum,
	campaignTemplateTypeEnum,
} from '@app/constants/modules/campaign';

export function getAllWorkspaceTemplate(
	templates: TemplateType[],
	name: string,
) {
	return templates?.filter((item: any) => item?.name === name);
}

export function getWorkspaceTemplatesByType(
	templates: TemplateType[],
	type: campaignTemplateTypeEnum,
) {
	return templates?.filter((template) => template?.type === type);
}

export function getWorkspaceTemplate(templates: TemplateType[], name: string) {
	return templates?.find((item: any) => item?.name === name);
}

export function getSmsTemplate(templates: TemplateType[]) {
	return templates?.find((template) => template?.name === 'sharedVideoSms');
}

export function getEmailTemplate(templates: TemplateType[]): TemplateType {
	return templates?.find(
		(template) => template?.name === 'sharedVideoEmail',
	) as TemplateType;
}

export function getSubjectEmailTitle(templates: TemplateType[]) {
	return getEmailTemplate(templates)?.title;
}

export function checkIsWithinSendingWindow(
	sendingDays: number[],
	sendingHours: [number, number],
	workspaceTimezone: string,
) {
	const workspaceDate = convertToTimezone(new Date(), workspaceTimezone);

	const currentDay = workspaceDate.getDay();
	const currentHour = workspaceDate.getHours();

	if (!sendingDays?.includes(currentDay)) return false;

	return (
		currentHour >= Math.min(...sendingHours) &&
		currentHour < Math.max(...sendingHours)
	);
}

export function checkEmailSetupCompleted(workspace: WorkspaceType) {
	if (!workspace) return false;

	const { subuserName, subuserApikey } = workspace;

	return !!subuserName && !!subuserApikey;
}

export function checkSMSSetupCompleted(workspace: WorkspaceType) {
	if (!workspace) return false;

	const { twilioSubaccountAuthToken, twilioSubaccountSid } = workspace;

	const hasSMSPlan = checkWorkspaceHasSMSPlan(workspace);

	return hasSMSPlan && !!twilioSubaccountAuthToken && !!twilioSubaccountSid;
}

export function checkWhatsAppSetupCompleted(workspace: WorkspaceType) {
	if (!workspace) return false;

	const {
		whatsappSetupCompleted,
		twilioSubaccountAuthToken,
		twilioSubaccountSid,
	} = workspace;

	return (
		!!twilioSubaccountAuthToken &&
		!!twilioSubaccountSid &&
		!!whatsappSetupCompleted
	);
}

export function checkWhatsAppIsEnabled(workspace: WorkspaceType) {
	if (!workspace) return false;

	const { whatsappEnabled } = workspace;

	return !!whatsappEnabled;
}

export function getWorkspaceIntegrationName(
	workspace?: WorkspaceType,
): ContactImportIntegrationNameEnum {
	return (
		workspace?.hotglueSettings?.flows?.contactImport?.integrationName || ''
	);
}

export function getWorkspaceAvailableContactsLists(workspace: WorkspaceType) {
	return (
		workspace?.hotglueSettings?.flows?.contactImport?.availableContactsLists ||
		[]
	);
}

export function getWorkspaceIntegrationDisplayName(
	workspace?: WorkspaceType,
	currentIntegrationName?: ContactImportIntegrationNameEnum,
): ContactImportIntegrationDisplayNameEnum {
	const integrationName = currentIntegrationName
		? currentIntegrationName
		: getWorkspaceIntegrationName(workspace);

	if (integrationName === ContactImportIntegrationNameEnum.HUBSPOT) {
		return ContactImportIntegrationDisplayNameEnum.HUBSPOT;
	}

	if (integrationName === ContactImportIntegrationNameEnum.HUBSPOT_BETA) {
		return ContactImportIntegrationDisplayNameEnum.HUBSPOT;
	}

	if (integrationName === ContactImportIntegrationNameEnum.SFTP) {
		return ContactImportIntegrationDisplayNameEnum.SFTP;
	}

	if (integrationName === ContactImportIntegrationNameEnum.SALESFORCE) {
		return ContactImportIntegrationDisplayNameEnum.SALESFORCE;
	}

	if (integrationName === ContactImportIntegrationNameEnum.DYNAMICS) {
		return ContactImportIntegrationDisplayNameEnum.DYNAMICS;
	}
	if (integrationName === ContactImportIntegrationNameEnum.SALESFORCE_SANDBOX) {
		return ContactImportIntegrationDisplayNameEnum.SALESFORCE_SANDBOX;
	}
	if (integrationName === ContactImportIntegrationNameEnum.BLACKBAUD) {
		return ContactImportIntegrationDisplayNameEnum.BLACKBAUD;
	}
	if (integrationName === ContactImportIntegrationNameEnum.ELLUCIAN_RECRUIT) {
		return ContactImportIntegrationDisplayNameEnum.ELLUCIAN_RECRUIT;
	}
	return '' as ContactImportIntegrationDisplayNameEnum;
}

export function checkWorkspaceHasIntegration(workspace: WorkspaceType) {
	return !!workspace?.hotglueSettings?.flows?.contactImport;
}

export function checkIsSftpIntegration(workspace: WorkspaceType) {
	return (
		getWorkspaceIntegrationName(workspace) ===
		ContactImportIntegrationNameEnum.SFTP
	);
}

export function getCustomAttributesFormatType(
	workspace: WorkspaceType,
	attributeName: string,
) {
	return workspace?.customAttributes?.find(
		(attribute) => attribute.name === attributeName,
	)?.format;
}

export function getIntegrationContactListById(
	integrationContactLists: IntegrationListType[],
	integrationId?: string,
) {
	return integrationContactLists?.find(
		(contactList: IntegrationListType) => contactList.id === integrationId,
	);
}

export function getWorkspaceContactListById(
	workspace: WorkspaceType,
	contactListId?: string,
): IntegrationListType {
	const contactLists = getWorkspaceAvailableContactsLists(workspace);

	return getIntegrationContactListById(
		contactLists,
		contactListId,
	) as IntegrationListType;
}

export function getWorkspacePlans(workspace: WorkspaceType) {
	return workspace?.settings?.plans && !R.isEmpty(workspace?.settings?.plans)
		? workspace?.settings?.plans
		: [];
}

export function checkWorkspaceHasSMSPlan(workspace: WorkspaceType) {
	const workspacePlans = getWorkspacePlans(workspace);
	return workspacePlans.includes(WorkspacePlansEnum.SMS) || false;
}

export function checkWorkspaceHasVideoMessagingPlan(workspace: WorkspaceType) {
	const workspacePlans = getWorkspacePlans(workspace);

	return workspacePlans.includes(WorkspacePlansEnum.VIDEO_MESSAGING) || false;
}

export function checkWorkspaceHasReelsPlan(workspace: WorkspaceType) {
	const workspacePlans = getWorkspacePlans(workspace);

	return workspacePlans.includes(WorkspacePlansEnum.REELS) || false;
}

export function checkWorkspaceHasEmbeddedReelsPlan(workspace: WorkspaceType) {
	const workspacePlans = getWorkspacePlans(workspace);

	return workspacePlans.includes(WorkspacePlansEnum.EMBEDDED_REELS) || false;
}

export function checkWorkspaceHasChatBotPlan(workspace: WorkspaceType) {
	const workspacePlans = getWorkspacePlans(workspace);

	return workspacePlans.includes(WorkspacePlansEnum.CHATBOT) || false;
}

export function checkWorkspaceIsChatbotOnly(workspace: WorkspaceType) {
	const workspacePlans = getWorkspacePlans(workspace);

	return (
		workspacePlans.length === 1 &&
		workspacePlans.includes(WorkspacePlansEnum.CHATBOT)
	);
}

export function checkWorkspaceHasOnlySMSPlan(workspace: WorkspaceType) {
	const workspacePlans = getWorkspacePlans(workspace);

	return (
		!R.isEmpty(workspacePlans) &&
		workspacePlans.length === 1 &&
		checkWorkspaceHasSMSPlan(workspace)
	);
}

export function checkWorkspaceHasOnlySMSandChatbotPlans(
	workspace: WorkspaceType,
) {
	const workspacePlans = getWorkspacePlans(workspace);

	return (
		!R.isEmpty(workspacePlans) &&
		workspacePlans.length === 2 &&
		checkWorkspaceHasSMSPlan(workspace) &&
		checkWorkspaceHasChatBotPlan(workspace)
	);
}

export function checkWorkspaceHasLegacy1dot0Plan(workspace: WorkspaceType) {
	const workspacePlans = getWorkspacePlans(workspace);
	return workspacePlans.includes(WorkspacePlansEnum.ADVANCE_1_0) || false;
}

/**
 * Normalizes `workspace.features` to a list of allowed feature-ID strings, support shapes the backend may serve during the rollout.
 */
export function getAllowedFeatureIds(workspace: WorkspaceType): string[] {
	const features = workspace?.features;
	if (!Array.isArray(features)) {
		return [];
	}
	return features.every((feature) => typeof feature === 'string')
		? (features as string[])
		: (features as WorkspaceFeatureStatus[])
				.filter((feature) => feature.allowed)
				.map((feature) => feature.featureID);
}

/**
 * Returns the full entitlement status for a single feature, or `undefined` when
 * the workspace carries no status for it. Tolerates both rollout shapes*/
export function getWorkspaceFeature(
	workspace: WorkspaceType,
	featureId: string,
): WorkspaceFeatureStatus | undefined {
	const features = workspace?.features;
	if (!Array.isArray(features) || features.length === 0) {
		return undefined;
	}
	if (features.every((feature) => typeof feature === 'string')) {
		return (features as string[]).includes(featureId)
			? { featureID: featureId, allowed: true, requirementGroups: [] }
			: undefined;
	}
	return (features as WorkspaceFeatureStatus[]).find(
		(feature) => feature.featureID === featureId,
	);
}

export function workspaceHasFeature(
	workspace: WorkspaceType,
	featureId: WorkspaceEntitlementFeatureEnum,
) {
	return getAllowedFeatureIds(workspace).includes(featureId);
}

export function checkWorkspaceHasAdvanceVideoEntitlement(
	workspace: WorkspaceType,
) {
	return getAllowedFeatureIds(workspace).includes(
		WorkspaceEntitlementFeatureEnum.ADVANCE_VIDEO_MESSAGING,
	);
}

export function checkWorkspaceHasCompetitiveStandingEntitlement(
	workspace: WorkspaceType,
) {
	return getAllowedFeatureIds(workspace).includes(
		WorkspaceEntitlementFeatureEnum.ADVANCE_COMPETITIVE_STANDING_MESSAGING,
	);
}

/**
 * Computes which campaign builders a workspace may use.
 *
 * - Video plan + advance entitlement → lite (Advance 2.0 partners).
 * - Video plan, no advance entitlement → legacy (new standalone workspaces).
 * - Reels or embedded-reels plan → legacy regardless of the video-messaging
 *   plan, so reels crews that never had a video plan are covered too. An
 *   advance-entitled workspace that also has a reels plan ends up dual
 *   (lite + legacy).
 * - Neither a video plan nor a reels plan → no builders.
 */
export function getAllowedCampaignBuilders({
	hasWorkspaceVideoPlan,
	hasAdvanceVideoEntitlement,
	hasAReelsPlan,
}: {
	hasWorkspaceVideoPlan: boolean;
	hasAdvanceVideoEntitlement: boolean;
	hasAReelsPlan: boolean;
}): CampaignBuilderEnum[] {
	const allowedBuilders = new Set<CampaignBuilderEnum>();

	if (hasWorkspaceVideoPlan) {
		allowedBuilders.add(
			hasAdvanceVideoEntitlement
				? CampaignBuilderEnum.LITE
				: CampaignBuilderEnum.LEGACY,
		);
	}

	if (hasAReelsPlan) {
		allowedBuilders.add(CampaignBuilderEnum.LEGACY);
	}

	return [...allowedBuilders];
}
